import { Mesh } from "@babylonjs/core/Meshes/mesh";
import { VertexData } from "@babylonjs/core/Meshes/mesh.vertexData";
import type { Scene } from "@babylonjs/core/scene";
import { cylinder, sphere, type PartBatch } from "../../../scene/core/meshKit";
import type { UvRect } from "./paint";

/**
 * Small geometry helpers for the casino's 3D: textured quads (cards, felts, signs, carpets) accumulated into one mesh,
 * and simple stylised figures (dealers, the bartender). Original geometry only.
 */
export interface P3 {
  x: number;
  y: number;
  z: number;
}

/** Accumulates textured quads into one mesh (rebuilt or updated in place). Two-sided materials make winding moot. */
export class QuadBatch {
  positions: number[] = [];
  normals: number[] = [];
  uvs: number[] = [];
  indices: number[] = [];

  get quads(): number {
    return this.positions.length / 12;
  }

  clear(): void {
    this.positions = [];
    this.normals = [];
    this.uvs = [];
    this.indices = [];
  }

  /** Corners bottom-left, bottom-right, top-right, top-left (as the texture reads). */
  quad(bl: P3, br: P3, tr: P3, tl: P3, uv: UvRect, normal: P3): this {
    const base = this.positions.length / 3;
    for (const p of [bl, br, tr, tl]) {
      this.positions.push(p.x, p.y, p.z);
      this.normals.push(normal.x, normal.y, normal.z);
    }
    this.uvs.push(uv.u0, uv.v0, uv.u1, uv.v0, uv.u1, uv.v1, uv.u0, uv.v1);
    this.indices.push(base, base + 1, base + 3, base + 1, base + 2, base + 3);
    return this;
  }

  /**
   * A flat quad lying on the floor / a table at height y: centre (x, z), size w x h, the texture's top pointing along
   * the unit direction (ux, uz).
   */
  flat(x: number, y: number, z: number, w: number, h: number, ux: number, uz: number, uv: UvRect): this {
    const rx = uz;
    const rz = -ux;
    const hw = w / 2;
    const hh = h / 2;
    const at = (a: number, b: number): P3 => ({ x: x + rx * a + ux * b, y, z: z + rz * a + uz * b });
    return this.quad(at(-hw, -hh), at(hw, -hh), at(hw, hh), at(-hw, hh), uv, { x: 0, y: 1, z: 0 });
  }

  /** An upright quad centred at (x, y, z) whose readable side faces the yaw `facing` (0 = north). */
  upright(x: number, y: number, z: number, w: number, h: number, facing: number, uv: UvRect): this {
    const nx = Math.sin(facing);
    const nz = Math.cos(facing);
    // Right-hand direction for a viewer looking at the face (opposite to the normal).
    const rx = -nz;
    const rz = nx;
    const hw = w / 2;
    const hh = h / 2;
    const at = (a: number, b: number): P3 => ({ x: x + rx * a, y: y + b, z: z + rz * a });
    return this.quad(at(-hw, -hh), at(hw, -hh), at(hw, hh), at(-hw, hh), uv, { x: nx, y: 0, z: nz });
  }

  /** Floor-aligned rectangle with world-space tiling (one texture repeat every `tile` metres). */
  floor(minX: number, minZ: number, maxX: number, maxZ: number, y: number, tile: number): this {
    const uv = { u0: minX / tile, v0: minZ / tile, u1: maxX / tile, v1: maxZ / tile };
    return this.quad({ x: minX, y, z: minZ }, { x: maxX, y, z: minZ }, { x: maxX, y, z: maxZ }, { x: minX, y, z: maxZ }, uv, { x: 0, y: 1, z: 0 });
  }

  /** A half disc lying flat at height y, straight edge to the north of (px, pz), arc to the south; UVs span the box. */
  halfDisc(px: number, pz: number, r: number, y: number, segments = 40): this {
    const base = this.positions.length / 3;
    this.positions.push(px, y, pz);
    this.normals.push(0, 1, 0);
    this.uvs.push(0.5, 1);
    for (let i = 0; i <= segments; i++) {
      // From east (angle 0) round the south to west.
      const a = (Math.PI * i) / segments;
      const dx = Math.cos(a) * r;
      const dz = -Math.sin(a) * r;
      this.positions.push(px + dx, y, pz + dz);
      this.normals.push(0, 1, 0);
      this.uvs.push(0.5 + dx / (2 * r), 1 + dz / r);
    }
    for (let i = 0; i < segments; i++) this.indices.push(base, base + 1 + i, base + 2 + i);
    return this;
  }

  build(name: string, scene: Scene, updatable = false): Mesh {
    const mesh = new Mesh(name, scene);
    this.applyTo(mesh, updatable);
    mesh.isPickable = false;
    return mesh;
  }

  applyTo(mesh: Mesh, updatable = true): void {
    const vd = new VertexData();
    vd.positions = this.positions;
    vd.normals = this.normals;
    vd.uvs = this.uvs;
    vd.indices = this.indices;
    vd.applyToMesh(mesh, updatable);
  }
}

/** A stylised standing figure (dealer, bartender), feet at (x, z), facing `facing`, into a vertex-colour batch. */
export function figure(scene: Scene, b: PartBatch, x: number, z: number, facing: number, look: { vest: string; shirt: string; skin: string; hair: string; tie: string }): void {
  const from = b.vertexCount;
  const pants = "#24262E";
  // Built facing north around (x, z), then turned about it.
  for (const side of [-1, 1]) {
    b.boxBase(0.15, 0.82, 0.17, pants, x + side * 0.1, 0, z);
    b.boxBase(0.17, 0.08, 0.26, "#15161B", x + side * 0.1, 0, z + 0.04);
  }
  b.boxBase(0.4, 0.62, 0.24, look.shirt, x, 0.82, z);
  b.boxBase(0.42, 0.5, 0.25, look.vest, x, 0.84, z + 0.005);
  b.box(0.08, 0.06, 0.03, look.tie, x, 1.38, z + 0.135);
  for (const side of [-1, 1]) {
    b.box(0.1, 0.5, 0.12, look.shirt, x + side * 0.26, 1.16, z + 0.02);
    b.box(0.09, 0.09, 0.1, look.skin, x + side * 0.26, 0.88, z + 0.04);
  }
  b.box(0.12, 0.1, 0.12, look.skin, x, 1.49, z);
  b.add(sphere(scene, "fig_head", 0.26, 8, look.skin, { pos: { x, y: 1.66, z: z + 0.01 } }));
  b.add(sphere(scene, "fig_hair", 0.28, 8, look.hair, { pos: { x, y: 1.71, z: z - 0.02 } }));
  b.rotateY(x, z, facing, from);
}

/** A bottle for the back bar (vertex colours). */
export function bottle(scene: Scene, b: PartBatch, x: number, y: number, z: number, color: string, h = 0.3): void {
  b.add(cylinder(scene, "bottle", { height: h * 0.68, diameter: 0.075, tessellation: 8 }, color, { pos: { x, y: y + h * 0.34, z } }));
  b.add(cylinder(scene, "bottleNeck", { height: h * 0.32, diameterTop: 0.025, diameterBottom: 0.05, tessellation: 6 }, color, { pos: { x, y: y + h * 0.84, z } }));
}
